<template>
  <div class="tab-rules">
    <div v-if="isLoading" class="text-center ma-12">
      <v-progress-circular
        size="48"
        width="4"
        indeterminate
        color="grey lighten-2"
      ></v-progress-circular>
    </div>

    <template v-else>
      <v-data-table
        :headers="headers"
        :items="rules"
        :search="search"
        @click:row="itemEdit"
        sort-by="name"
        item-key="name"
        class="elevation-0 general-table"
      >
        <template v-slot:header.name="{ header }">
          <lang t="DERIVATION_RULES-CALC-TBL-HEADER-NAME"></lang>
        </template>

        <template v-slot:header.status="{ header }">
          <lang t="DERIVATION_RULES-CALC-TBL-HEADER-STATUS"></lang>
        </template>

        <template v-slot:header.edit="{ header }">
          <lang t="DERIVATION_RULES-CALC-TBL-HEADER-EDIT"></lang>
        </template>

        <template v-slot:header.updatedBy.username="{ header }">
          <lang t="DERIVATION_RULES-CALC-TBL-HEADER-UPDATED_BY"></lang>
        </template>

        <template v-slot:header.actions="{ header }">
          <lang t="DERIVATION_RULES-CALC-TBL-HEADER-ACTIONS"></lang>
        </template>


      <template v-slot:footer.page-text="props" v-if="(rules.length > 0)">
        <display-number :number="props.pageStart" />-<display-number :number="props.pageStop" /> {{ $t("of") }}
        <display-number :number="rules.length" />
      </template>

        <template v-slot:top>
          <div class="d-flex align-center flex-wrap py-2 mb-3">
            <v-text-field
              clearable
              class="general-table__search"
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              :label="$t('GLOBAL-DO_SEARCH')"
              solo
              flat
              dense
              hide-details
            ></v-text-field>

            <v-spacer></v-spacer>

            <Button type="outside-label" icon="mdi-plus" :label="$t('DERIVATION_RULES-BTN-NEW_RULE')" @click="itemNew" />

          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <template
            v-if="item.status == 'DRAFT' && item.settings_published != null"
          >
            ACTIVE (DRAFT)
          </template>
          <template v-else>
            {{ item.status }}
          </template>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="pa-1"
            color="grey darken-2"
            @click.stop.prevent="itemExport(item)"
            >mdi-database-export</v-icon
          >
          <v-icon
            small
            class="pa-1"
            color="grey darken-2"
            @click.stop.prevent="itemReset(item)"
            >mdi-lock-reset</v-icon
          >
          <v-icon
            small
            class="pa-1"
            color="grey darken-2"
            :disabled="!item.isDeleteable"
            @click.stop.prevent="itemDelete(item)"
            >mdi-trash-can-outline</v-icon
          >
        </template>
      </v-data-table>

      <v-dialog
        v-model="dialog.show"
        width="auto"
        :fullscreen="true"
        :persistent="false"
      >
        <rules-editor
          :key="editedItem"
          :calculation="editedItem"
          :ruleSettings="ruleSettings"
          :constants="constants"
          @closeModal="closeModal"
        />

      </v-dialog>
    </template>
  </div>
</template>

<script>
import RulesEditor from "./RulesEditor";
import Button from '../../../components/ui-elements/Button.vue';
import DisplayNumber from "../../../components/DisplayNumber.vue";

export default {
  name: 'TabRules',
  components: {
    RulesEditor,
    Button,
    DisplayNumber
  },

  data: () => ({
    isSaving: false,
    isLoading: true,
    search: "",
    headers: [
      {
        text: "ADP Program",
        align: "start",
        sortable: true,
        value: "name",
        class: 'general-table__th--3'
      },
      {
        text: "Status",
        align: "start",
        sortable: true,
        value: "status",
        class: 'general-table__th--3'
      },

      {
        text: "UpdatedBy",
        align: "start",
        sortable: false,
        value: "updatedBy.username",
        class: 'general-table__th--3'
      },

      {
        text: "Actions",
        align: "end",
        sortable: false,
        value: "actions",
        width: "120px",
      },
    ],
    rules: null,
    ruleSettings: null,
    constants: null,

    dialog: {
      show: false,
      isSaving: false,
    },

    editedIndex: -1,

    editedItem: {
      name: null,
      status: null,
      parent_id: null,
      settings: [],
    },

    defaultItem: {
      name: null,
      status: null,
      parent_id: null,
      settings: [],
    },
  }),

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      var self = this;

      const getRules = () => this.$axios.get("calculationrules");
      const getRuleSettings = () =>
        this.$axios.get("calculationrules/settings");
      const getConstants = () => this.$axios.get("constants");

      Promise.all([getRules(), getRuleSettings(), getConstants()])
        .then(([rules, settings, constants]) => {
          this.rules = rules.data;
          this.ruleSettings = settings.data;
          this.constants = constants.data;

          this.isLoading = false;
        })
        .catch((error) => {});
    },

    itemDelete(item) {
      this.$confirm(
        this.$t("GLOBAL-CONFIRMATION-DELETE-MSG", { NAME: item.name }),
        {
          title: this.$t("GLOBAL-CONFIRMATION-DELETE-TITLE"),
          buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
          buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
        }
      ).then((res) => {
        if (res) {
          this.axios.delete("calculationrules/" + item.id).then((res) => {
            this.init();
            this.$store.dispatch("setSnackbar", {
              text: this.$t("GLOBAL-SUCCESSFULLY_DELETED"),
            });
          });
        }
      });
    },

    itemNew() {
      this.editedIndex = -1;

      let newItem = {
        name: "NEW RULE ",
        status: "DRAFT",
        settings: {
          stages: [],
        },
      };

      this.editedItem = JSON.parse(JSON.stringify(newItem));
      this.dialog.show = true;
    },

    itemEdit(item) {
      this.editedIndex = this.rules.indexOf(item);

      item.settings.stages.forEach((stage) => {
        stage.steps.forEach((step) => {
          if (Array.isArray(step.if)) {
            step.if = {};
          }
        });
      });

      this.editedItem = Object.assign({}, item);
     
      this.dialog.show = true;
    },

    itemExport(item){
       // Convert data to JSON string
      const jsonString = JSON.stringify(item.settings_published, null, 2);

      // Create a Blob with JSON data
      const blob = new Blob([jsonString], { type: "application/json" });

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a temporary <a> element
      const a = document.createElement("a");
      a.href = url;
      a.download = "settings.json"; // Set the filename
      document.body.appendChild(a); // Append to document
      a.click(); // Trigger the download
      document.body.removeChild(a); // Clean up

      // Revoke the Object URL to free up memory
      URL.revokeObjectURL(url);
    },

    itemReset(item) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-RESET-MSG"), {
        title: this.$t("GLOBAL-CONFIRMATION-RESET-TITLE"),
        buttonTrueText: this.$t("ATTRIBUTE-OVERRIDE-RESET"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.axios
            .get("calculationrules/" + item.id + "/revert")
            .then((res) => {
              this.$store.dispatch("setSnackbar", {
                text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_REVERTED"),
              });
              this.init();
            });
        }
      });
    },

    _fin() {
      this.dialog.isSaving = false;
      this.$store.dispatch("setSnackbar", {
        text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED"),
      });
      this.init();
      this.closeModal();
    },

    closeModal() {
      this.dialog.show = false;
      this.init();

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
  },
};
</script>

<style lang="scss">

</style>
